/**
 * Canonical breadcrumb crumbs for navigable destinations.
 *
 * Labels must match the sidenav labels in
 * `app/components/layout/sidebar/hooks.ts` — the breadcrumb trail mirrors the
 * sidenav information architecture, so a destination is spelled exactly one
 * way everywhere (locked by breadcrumbs.test.ts).
 */

import { paths } from "app/constants/paths";

/** A crumb that is always clickable — `target` is mandatory. */
export interface BreadcrumbLink {
  label: string;
  target: string;
}

// Top-level sidenav destinations.
export const RESOURCES_BREADCRUMB: BreadcrumbLink = {
  label: "Resources",
  target: paths.resources,
};
export const ACCOUNTS_BREADCRUMB: BreadcrumbLink = {
  label: "Accounts",
  target: paths.adminAccounts,
};
export const BINDINGS_BREADCRUMB: BreadcrumbLink = {
  label: "Bindings",
  target: paths.adminBindings,
};
export const POLICIES_BREADCRUMB: BreadcrumbLink = {
  label: "Policies",
  target: paths.adminPolicies,
};
export const SETTINGS_BREADCRUMB: BreadcrumbLink = {
  label: "Settings",
  target: paths.users,
};
export const NOTIFICATIONS_BREADCRUMB: BreadcrumbLink = {
  label: "Notifications",
  target: paths.notificationsOverview,
};

// Query-param tab views used as parent crumbs by their child pages.
export const ACCOUNT_GROUPS_BREADCRUMB: BreadcrumbLink = {
  label: "Account Groups",
  target: `${paths.adminAccounts}?tab=account-groups`,
};
export const POLICY_COLLECTIONS_BREADCRUMB: BreadcrumbLink = {
  label: "Policy Collections",
  target: `${paths.adminPolicies}?tab=policy-collections`,
};
export const POLICY_REPOSITORIES_BREADCRUMB: BreadcrumbLink = {
  label: "Policy Repositories",
  target: `${paths.adminPolicies}?tab=repositories`,
};

// Mirrors the sidenav targets + activePaths in sidebar/hooks.ts: every
// /admin/* URL belongs to one of the top-level Accounts/Bindings/Policies
// items ("Admin" itself is not a sidenav concept and never appears in trails).
const ADMIN_SECTIONS: { crumb: BreadcrumbLink; pathPrefixes: string[] }[] = [
  {
    crumb: ACCOUNTS_BREADCRUMB,
    pathPrefixes: [paths.adminAccounts, paths.adminAccountGroups],
  },
  { crumb: BINDINGS_BREADCRUMB, pathPrefixes: [paths.adminBindings] },
  {
    crumb: POLICIES_BREADCRUMB,
    pathPrefixes: [
      paths.adminPolicies,
      paths.adminPolicyCollections,
      paths.adminRepositories,
    ],
  },
];

/** Root crumb for a page under /admin/* — the sidenav item that owns the path. */
export function getAdminSectionCrumb(
  pathname: string,
): BreadcrumbLink | undefined {
  return ADMIN_SECTIONS.find((section) =>
    section.pathPrefixes.some((prefix) => pathname.startsWith(prefix)),
  )?.crumb;
}
